import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const STATUS_BADGE = {
    pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    completed: "bg-[#00b67a]/15 text-[#00b67a] border-[#00b67a]/30",
    rejected: "bg-red-500/15 text-red-400 border-red-500/30",
    cancelled: "bg-white/[0.06] text-muted-foreground border-white/10",
};
const STATUS_TR = { pending: "bekliyor", completed: "tamamlandı", rejected: "reddedildi (iade)", cancelled: "iptal (iade)" };

export function useTopupRequests() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/ad-accounts/topups");
            setItems(data);
        } catch {} finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    return { items, loading, load };
}

export default function AdminTopupRequests({ items, loading, reload }) {
    const [filter, setFilter] = useState("pending");
    const [reject, setReject] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(null);

    const visible = filter === "all" ? items : items.filter((x) => x.status === filter);

    const act = async (id, status, adminNote) => {
        setBusy(id);
        try {
            await api.patch(`/admin/ad-accounts/topups/${id}`, { status, note: adminNote || null });
            toast.success(status === "completed" ? "Top-up tamamlandı — hesaba yüklendi" : "Reddedildi — tutar cüzdana iade edildi");
            setReject(null); setNote("");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "İşlem başarısız");
        } finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-topups" className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                {["pending", "completed", "rejected", "cancelled", "all"].map((f) => (
                    <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`admin-topups-filter-${f}`} data-active={filter === f}
                        className={`h-8 px-3 rounded-full border text-xs font-mono transition-colors ${filter === f ? "border-primary/50 bg-primary/[0.12] text-primary" : "border-white/10 text-muted-foreground hover:border-white/25"}`}>
                        {f === "all" ? "tümü" : STATUS_TR[f]} · {f === "all" ? items.length : items.filter((x) => x.status === f).length}
                    </button>
                ))}
                <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="admin-topups-refresh" className="ml-auto h-8 border-white/10 text-xs">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Yenile
                </Button>
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left p-4">Hesap</th>
                            <th className="text-left p-4 hidden md:table-cell">Kullanıcı</th>
                            <th className="text-right p-4">Ödenen → Hesaba</th>
                            <th className="text-left p-4">Durum</th>
                            <th className="text-left p-4 hidden lg:table-cell">Tarih</th>
                            <th className="text-right p-4">İşlem</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {visible.map((x) => (
                            <tr key={x.id} data-testid={`admin-topup-row-${x.id}`}>
                                <td className="p-4">
                                    <p className="font-medium">{x.account_name || "-"}</p>
                                    <p className="text-xs font-mono text-muted-foreground uppercase">{x.platform} · {x.account_type || "std"} · {x.vertical}</p>
                                </td>
                                <td className="p-4 hidden md:table-cell text-xs font-mono">{x.user_email || x.user_id.slice(0, 8)}</td>
                                <td className="p-4 text-right font-mono tabular-nums">
                                    <p data-testid={`admin-topup-net-${x.id}`}><span className="text-muted-foreground">{money(x.amount)}</span> → <span className="text-emerald-300 font-semibold">{money(x.net_amount)}</span></p>
                                    <p className="text-[11px] text-muted-foreground">komisyon {money(x.fee_amount)} @{Number(x.fee_percent).toFixed(2)}%</p>
                                </td>
                                <td className="p-4">
                                    <span data-testid={`admin-topup-status-${x.id}`} className={`inline-flex border rounded-full px-2 py-0.5 text-[11px] font-mono ${STATUS_BADGE[x.status] || ""}`}>{STATUS_TR[x.status] || x.status}</span>
                                    {x.admin_note && <p className="text-[11px] text-muted-foreground mt-1 max-w-[220px] truncate" title={x.admin_note}>{x.admin_note}</p>}
                                </td>
                                <td className="p-4 hidden lg:table-cell text-xs font-mono text-muted-foreground">{new Date(x.created_at).toLocaleString("tr-TR")}</td>
                                <td className="p-4 text-right">
                                    {x.status === "pending" ? (
                                        <div className="inline-flex gap-2">
                                            <Button size="sm" onClick={() => act(x.id, "completed")} disabled={busy === x.id} data-testid={`admin-topup-complete-${x.id}`} className="h-8 bg-[#00b67a] hover:bg-[#00b67a]/90 text-white text-xs">
                                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Tamamla
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => { setReject(x); setNote(""); }} disabled={busy === x.id} data-testid={`admin-topup-reject-${x.id}`} className="h-8 border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs">
                                                <XCircle className="w-3.5 h-3.5 mr-1" /> Reddet
                                            </Button>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-muted-foreground font-mono">{x.completed_at ? new Date(x.completed_at).toLocaleString("tr-TR") : x.refunded_at ? `iade ${new Date(x.refunded_at).toLocaleString("tr-TR")}` : "-"}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {visible.length === 0 && <p className="p-12 text-center text-sm text-muted-foreground" data-testid="admin-topups-empty">Bu filtrede top-up talebi yok.</p>}
            </div>

            <Dialog open={Boolean(reject)} onOpenChange={(o) => !o && setReject(null)}>
                <DialogContent className="bg-card border-white/10 max-w-md" data-testid="admin-topup-reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Top-up talebini reddet</DialogTitle>
                        <DialogDescription>{reject ? `${money(reject.amount)} kullanıcının cüzdanına iade edilir ve bildirim gönderilir.` : ""}</DialogDescription>
                    </DialogHeader>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Neden (opsiyonel)" data-testid="admin-topup-reject-note" className="bg-background border-white/10" />
                    <Button onClick={() => act(reject.id, "rejected", note)} disabled={busy === reject?.id} data-testid="admin-topup-reject-confirm" className="w-full bg-red-600 hover:bg-red-600/90 text-white">Reddet & iade et</Button>
                </DialogContent>
            </Dialog>
        </div>
    );
}
